import { supabase } from '@/utils/supabase';

/**
 * Service API pour les appels aux fonctions Netlify
 * Centralise les appels API avec gestion d'erreurs
 */

export interface APIError {
  message: string;
  code?: string;
  status: number;
  details?: any;
}

export class APIService {
  private static readonly BASE_URL = '/.netlify/functions';
  private static readonly TIMEOUT = 30000; // 30 secondes

  /**
   * Récupérer le token d'authentification
   */
  private static async getAuthToken(): Promise<string | null> {
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token || null;
    
    return token;
  }

  /**
   * Appel API générique
   */
  static async call<T = any>(
    endpoint: string,
    options: {
      method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
      body?: any;
      headers?: Record<string, string>;
      requireAuth?: boolean;
      timeout?: number;
    } = {}
  ): Promise<T> {
    const {
      method = 'GET',
      body,
      headers = {},
      requireAuth = true,
      timeout = this.TIMEOUT
    } = options;

    // Construire l'URL
    const url = endpoint.startsWith('/')
      ? `${this.BASE_URL}${endpoint}`
      : `${this.BASE_URL}/${endpoint}`;

    // Headers par défaut
    const defaultHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Ajouter le token d'authentification si nécessaire
    if (requireAuth) {
      const token = await this.getAuthToken();
      if (token) {
        defaultHeaders['Authorization'] = `Bearer ${token}`;
      } else if (requireAuth) {
        throw new Error('Authentication required');
      }
    }

    // Fusionner les headers
    const requestHeaders = { ...defaultHeaders, ...headers };

    // Options de la requête
    const requestOptions: RequestInit = {
      method,
      headers: requestHeaders,
      credentials: 'same-origin'
    };

    // Ajouter le body pour les méthodes non-GET
    if (method !== 'GET' && body) {
      requestOptions.body = JSON.stringify(body);
    }

    // Timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    requestOptions.signal = controller.signal;

    try {
      const response = await fetch(url, requestOptions);
      clearTimeout(timeoutId);

      // Vérifier si la réponse est OK
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        throw {
          message: errorData.error?.message || errorData.message || `Request failed with status ${response.status}`,
          code: errorData.error?.code || errorData.code || 'API_ERROR',
          status: response.status,
          details: errorData.error?.details || errorData
        };
      }

      // Parser la réponse
      const result = await response.json();
      return result;
      
    } catch (error: any) {
      console.error('API Call Error:', error);
      
      // Gérer les erreurs d'abort
      if (error.name === 'AbortError') {
        throw {
          message: 'Request timeout',
          code: 'TIMEOUT',
          status: 408
        };
      }

      // Gérer les erreurs réseau
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        throw {
          message: 'Network error',
          code: 'NETWORK_ERROR',
          status: 0
        };
      }

      // Propager les erreurs API
      if (error.status) {
        throw error;
      }

      // Erreur générique
      throw {
        message: error.message || 'Unknown error',
        code: 'UNKNOWN_ERROR',
        status: 500
      };
    }
  }

  /**
   * GET request
   */
  static async get<T = any>(
    endpoint: string,
    options: Omit<Parameters<typeof this.call>[1], 'method'> = {}
  ): Promise<T> {
    return this.call<T>(endpoint, { ...options, method: 'GET' });
  }

  /**
   * POST request
   */
  static async post<T = any>(
    endpoint: string,
    body: any,
    options: Omit<Parameters<typeof this.call>[1], 'method' | 'body'> = {}
  ): Promise<T> {
    return this.call<T>(endpoint, { ...options, method: 'POST', body });
  }

  /**
   * PUT request
   */
  static async put<T = any>(
    endpoint: string,
    body: any,
    options: Omit<Parameters<typeof this.call>[1], 'method' | 'body'> = {}
  ): Promise<T> {
    return this.call<T>(endpoint, { ...options, method: 'PUT', body });
  }

  /**
   * DELETE request
   */
  static async delete<T = any>(
    endpoint: string,
    options: Omit<Parameters<typeof this.call>[1], 'method'> = {}
  ): Promise<T> {
    return this.call<T>(endpoint, { ...options, method: 'DELETE' });
  }
}

// API spécifiques
export const AuthAPI = {
  login: (email: string, password: string) => 
    APIService.post('auth-login', { email, password }, { requireAuth: false }),
  
  register: (email: string, password: string, firstName?: string, lastName?: string) =>
    APIService.post('auth-register', { 
      email, 
      password, 
      first_name: firstName,
      last_name: lastName 
    }, { requireAuth: false }),
  
  logout: (session_token: string) => 
    APIService.post('auth-logout', { session_token }),
  
  refresh: (refresh_token: string) => 
    APIService.post('auth-refresh', { refresh_token }, { requireAuth: false }),
  
  getProfile: () => 
    APIService.get('auth-profile')
};

export const DPEAPI = {
  analyze: (dpeId: string, dpeData?: any) => 
    APIService.post('dpe-analyze', { dpeId, dpeData }),
  
  searchADEME: (numeroDPE: string) => {
    return APIService.post('dpe-api', { numeroDPE });
  },
  
  searchByAddress: (address: string, postalCode?: string, city?: string, limit?: number) => 
    APIService.post('ademe-search', { address, postalCode, city, limit }),

  getStatus: (analysisId: string) =>
    APIService.get(`dpe-status?id=${analysisId}`),
    
  retry: (analysisId: string) =>
    APIService.post(`dpe-retry`, { analysisId }),
    
  cancel: (analysisId: string) =>
    APIService.post(`dpe-cancel`, { analysisId }),
    
  getQuestions: (dpe_id: string, analysis_id?: string) => 
    APIService.post('dpe-get-questions', { dpe_id, analysis_id }),
  
  submitAnswers: (session_id: string, answers: any[], complete_session?: boolean) => {
    return APIService.post('dpe-submit-answers', { session_id, answers, complete_session })
  },
  
  // ✅ FONCTION CORRIGÉE : Récupérer une session de questionnaire complète
  getQuestionnaireSession: (sessionId: string) => {
    return APIService.get(`questionnaire-session/${sessionId}`)
  }
};

export default APIService;